/**
 * Startup Investment & ROI Tracker
 * 
 * Comprehensive dashboard for tracking startup costs, capital recovery,
 * and ROI calculations. Part of the Investor-Ready Business Assessment.
 */
import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  DollarSign, TrendingUp, TrendingDown, PiggyBank, Calculator,
  Plus, Trash2, Edit2, Save, X, RefreshCw, Download, ChevronDown,
  Building2, Wrench, Armchair, Laptop, Truck, Hammer, FileText,
  Megaphone, Users, Package, Wallet, Folder, Target, Clock,
  AlertTriangle, CheckCircle2, BarChart3, PieChart, Calendar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Format currency with $ for admin area
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

const formatCurrencyFull = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount || 0);
};

// Category icons mapping
const categoryIcons = {
  equipment: Wrench,
  furniture: Armchair,
  technology: Laptop,
  vehicles: Truck,
  renovations: Hammer,
  legal: FileText,
  marketing: Megaphone,
  training: Users,
  inventory: Package,
  working_capital: Wallet,
  other: Folder
};

// Category labels
const categoryLabels = {
  equipment: "Equipment & Machinery",
  furniture: "Furniture & Fixtures",
  technology: "Technology",
  vehicles: "Vehicles",
  renovations: "Leasehold Improvements",
  legal: "Legal & Professional",
  marketing: "Marketing & Branding",
  training: "Training & Hiring",
  inventory: "Initial Inventory",
  working_capital: "Working Capital",
  other: "Other"
};

// Startup Cost Form Modal
function CostFormModal({ isOpen, onClose, onSave, editingCost }) {
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    category: 'equipment',
    item_name: '',
    description: '',
    vendor: '',
    quantity: 1,
    unit_cost: '',
    payment_method: '',
    invoice_number: '',
    asset_type: 'capital',
    useful_life_years: '',
    notes: ''
  });

  useEffect(() => {
    if (editingCost) {
      setFormData({
        date: editingCost.date || new Date().toISOString().split('T')[0],
        category: editingCost.category || 'equipment',
        item_name: editingCost.item_name || '',
        description: editingCost.description || '',
        vendor: editingCost.vendor || '',
        quantity: editingCost.quantity || 1,
        unit_cost: editingCost.unit_cost || '',
        payment_method: editingCost.payment_method || '',
        invoice_number: editingCost.invoice_number || '',
        asset_type: editingCost.asset_type || 'capital',
        useful_life_years: editingCost.useful_life_years || '',
        notes: editingCost.notes || ''
      });
    } else {
      setFormData({
        date: new Date().toISOString().split('T')[0],
        category: 'equipment',
        item_name: '',
        description: '',
        vendor: '',
        quantity: 1,
        unit_cost: '',
        payment_method: '',
        invoice_number: '',
        asset_type: 'capital',
        useful_life_years: '',
        notes: ''
      });
    }
  }, [editingCost, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.item_name || !formData.unit_cost) {
      toast.error('Please fill in required fields');
      return;
    }
    onSave({
      ...formData,
      unit_cost: parseFloat(formData.unit_cost),
      quantity: parseInt(formData.quantity) || 1,
      useful_life_years: formData.useful_life_years ? parseInt(formData.useful_life_years) : null
    });
  };

  const totalCost = (parseFloat(formData.unit_cost) || 0) * (parseInt(formData.quantity) || 1);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingCost ? 'Edit Startup Cost' : 'Add Startup Cost'}</DialogTitle>
          <DialogDescription>
            Record a startup expense or capital asset
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date">Purchase Date *</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="category">Category *</Label>
              <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(categoryLabels).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="item_name">Item Name *</Label>
            <Input
              id="item_name"
              value={formData.item_name}
              onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
              placeholder="e.g., La Marzocco Espresso Machine"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Details about the purchase..."
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="vendor">Vendor/Supplier</Label>
              <Input
                id="vendor"
                value={formData.vendor}
                onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                placeholder="Where purchased"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="invoice_number">Invoice/Receipt #</Label>
              <Input
                id="invoice_number"
                value={formData.invoice_number}
                onChange={(e) => setFormData({ ...formData, invoice_number: e.target.value })}
                placeholder="Reference number"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="quantity">Quantity</Label>
              <Input
                id="quantity"
                type="number"
                min="1"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="unit_cost">Unit Cost *</Label>
              <Input
                id="unit_cost"
                type="number"
                min="0"
                step="0.01"
                value={formData.unit_cost}
                onChange={(e) => setFormData({ ...formData, unit_cost: e.target.value })}
                placeholder="0.00"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Total Cost</Label>
              <div className="h-10 px-3 py-2 bg-muted rounded-md text-lg font-semibold">
                {formatCurrencyFull(totalCost)}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="asset_type">Asset Type</Label>
              <Select value={formData.asset_type} onValueChange={(v) => setFormData({ ...formData, asset_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="capital">Capital Asset (Depreciable)</SelectItem>
                  <SelectItem value="expense">Pre-Opening Expense</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.asset_type === 'capital' && (
              <div className="space-y-2">
                <Label htmlFor="useful_life">Useful Life (years)</Label>
                <Input
                  id="useful_life"
                  type="number"
                  min="1"
                  max="50"
                  value={formData.useful_life_years}
                  onChange={(e) => setFormData({ ...formData, useful_life_years: e.target.value })}
                  placeholder="e.g., 10"
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="payment_method">Payment Method</Label>
            <Select value={formData.payment_method} onValueChange={(v) => setFormData({ ...formData, payment_method: v })}>
              <SelectTrigger>
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="credit_card">Credit Card</SelectItem>
                <SelectItem value="financing">Financing/Loan</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes..."
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit">
              <Save className="w-4 h-4 mr-2" />
              {editingCost ? 'Update' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Funding Source Form Modal
function FundingFormModal({ isOpen, onClose, onSave }) {
  const [formData, setFormData] = useState({
    source_name: '',
    source_type: 'personal',
    amount: '',
    date_received: new Date().toISOString().split('T')[0],
    interest_rate: '',
    repayment_terms: '',
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      setFormData({
        source_name: '',
        source_type: 'personal',
        amount: '',
        date_received: new Date().toISOString().split('T')[0],
        interest_rate: '',
        repayment_terms: '',
        notes: ''
      });
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.source_name || !formData.amount) {
      toast.error('Please fill in required fields');
      return;
    }
    onSave({
      ...formData,
      amount: parseFloat(formData.amount),
      interest_rate: formData.interest_rate ? parseFloat(formData.interest_rate) : 0
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Funding Source</DialogTitle>
          <DialogDescription>
            Record how your startup was funded
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="source_name">Source Name *</Label>
            <Input
              id="source_name"
              value={formData.source_name}
              onChange={(e) => setFormData({ ...formData, source_name: e.target.value })}
              placeholder="e.g., Personal Savings, ANZ Business Loan"
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="source_type">Type</Label>
              <Select value={formData.source_type} onValueChange={(v) => setFormData({ ...formData, source_type: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="personal">Personal Savings</SelectItem>
                  <SelectItem value="loan">Bank Loan</SelectItem>
                  <SelectItem value="investor">Investor</SelectItem>
                  <SelectItem value="grant">Grant</SelectItem>
                  <SelectItem value="family">Family/Friends</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="amount">Amount *</Label>
              <Input
                id="amount"
                type="number"
                min="0"
                step="0.01"
                value={formData.amount}
                onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                placeholder="0.00"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date_received">Date Received</Label>
              <Input
                id="date_received"
                type="date"
                value={formData.date_received}
                onChange={(e) => setFormData({ ...formData, date_received: e.target.value })}
              />
            </div>
            {(formData.source_type === 'loan' || formData.source_type === 'investor') && (
              <div className="space-y-2">
                <Label htmlFor="interest_rate">Interest Rate (%)</Label>
                <Input
                  id="interest_rate"
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  value={formData.interest_rate}
                  onChange={(e) => setFormData({ ...formData, interest_rate: e.target.value })}
                  placeholder="0.0"
                />
              </div>
            )}
          </div>

          {(formData.source_type === 'loan' || formData.source_type === 'investor') && (
            <div className="space-y-2">
              <Label htmlFor="repayment_terms">Repayment Terms</Label>
              <Textarea
                id="repayment_terms"
                value={formData.repayment_terms}
                onChange={(e) => setFormData({ ...formData, repayment_terms: e.target.value })}
                placeholder="e.g., Monthly payments of $500 for 36 months"
                rows={2}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              placeholder="Additional notes..."
              rows={2}
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit">
              <Save className="w-4 h-4 mr-2" />
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// Main Component
export default function StartupInvestmentTab() {
  const [dashboard, setDashboard] = useState(null);
  const [costs, setCosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCostModal, setShowCostModal] = useState(false);
  const [showFundingModal, setShowFundingModal] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await api.get('/admin/finance/startup/dashboard');
      setDashboard(res.data);
    } catch (err) {
      console.error('Failed to fetch dashboard:', err);
      toast.error('Failed to load dashboard');
    }
  }, []);

  const fetchCosts = useCallback(async () => {
    try {
      const res = await api.get('/admin/finance/startup/costs');
      setCosts(res.data.costs || []);
    } catch (err) {
      console.error('Failed to fetch costs:', err);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    await Promise.all([fetchDashboard(), fetchCosts()]);
    setLoading(false);
  }, [fetchDashboard, fetchCosts]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  const handleSaveCost = async (costData) => {
    try {
      if (editingCost) {
        await api.put(`/admin/finance/startup/costs/${editingCost.cost_id}`, costData);
      } else {
        await api.post('/admin/finance/startup/costs', costData);
      }
      toast.success(editingCost ? 'Cost updated' : 'Cost added');
      setShowCostModal(false);
      setEditingCost(null);
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save cost');
    }
  };

  const handleDeleteCost = async (costId) => {
    if (!confirm('Delete this startup cost?')) return;
    try {
      await api.delete(`/admin/finance/startup/costs/${costId}`);
      toast.success('Cost deleted');
      loadAll();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleSaveFunding = async (fundingData) => {
    try {
      await api.post('/admin/finance/startup/funding', fundingData);
      toast.success('Funding source added');
      setShowFundingModal(false);
      loadAll();
    } catch (err) {
      toast.error('Failed to save funding');
    }
  };

  const handleDeleteFunding = async (fundingId) => {
    if (!confirm('Delete this funding source?')) return;
    try {
      await api.delete(`/admin/finance/startup/funding/${fundingId}`);
      toast.success('Funding deleted');
      loadAll();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const inv = dashboard?.investment_summary || {};
  const recovery = dashboard?.capital_recovery || {};
  const payback = dashboard?.payback_projection || {};
  const depreciation = dashboard?.depreciation || {};

  return (
    <div className="space-y-6" data-testid="startup-investment-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Startup Investment Tracker</h2>
          <p className="text-muted-foreground">Track your initial investment and capital recovery</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={loadAll}>
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button onClick={() => { setEditingCost(null); setShowCostModal(true); }}>
            <Plus className="w-4 h-4 mr-2" />
            Add Cost
          </Button>
        </div>
      </div>

      {/* Investment Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-500/20 rounded-xl">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Investment</p>
                <p className="text-2xl font-bold text-foreground">{formatCurrency(inv.total_investment)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-500/20 rounded-xl">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Recovered</p>
                <p className="text-2xl font-bold text-green-600">{formatCurrency(recovery.recovered)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-amber-500/20 rounded-xl">
                <Target className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Remaining</p>
                <p className="text-2xl font-bold text-amber-600">{formatCurrency(recovery.remaining)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-purple-500/20 rounded-xl">
                <Clock className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Payback ETA</p>
                <p className="text-lg font-bold text-foreground">
                  {payback.projected_date || 'N/A'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Capital Recovery Progress */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PiggyBank className="w-5 h-5" />
            Capital Recovery Progress
          </CardTitle>
          <CardDescription>
            Track your progress toward recovering your initial investment
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <div className="flex justify-between text-sm">
              <span>Recovery Progress</span>
              <span className="font-semibold">{recovery.recovery_percentage?.toFixed(1) || 0}%</span>
            </div>
            <Progress value={recovery.recovery_percentage || 0} className="h-4" />
            <div className="flex justify-between text-xs text-muted-foreground">
              <span>{formatCurrency(recovery.recovered)} recovered</span>
              <span>{formatCurrency(recovery.remaining)} remaining</span>
            </div>
          </div>

          {payback.months_remaining > 0 && (
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">At current average monthly profit of {formatCurrency(payback.avg_monthly_profit)}:</p>
              <p className="text-lg font-semibold mt-1">
                Full recovery projected in <span className="text-primary">{payback.months_remaining} months</span> ({payback.projected_date})
              </p>
            </div>
          )}

          {recovery.remaining <= 0 && (
            <div className="p-4 bg-green-100 dark:bg-green-900/30 rounded-lg flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
              <div>
                <p className="font-semibold text-green-700 dark:text-green-400">Investment Fully Recovered!</p>
                <p className="text-sm text-green-600">You've recovered your entire initial investment.</p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tabs for detailed views */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-4 w-full max-w-lg">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="costs">Costs</TabsTrigger>
          <TabsTrigger value="funding">Funding</TabsTrigger>
          <TabsTrigger value="scenarios">Scenarios</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {/* Category Breakdown */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Investment by Category</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {dashboard?.by_category && Object.entries(dashboard.by_category).map(([key, cat]) => {
                  const IconComponent = categoryIcons[key] || Folder;
                  return (
                    <div key={key} className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
                      <div className="p-2 bg-background rounded-lg">
                        <IconComponent className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{cat.label}</p>
                        <p className="text-xs text-muted-foreground">{cat.count} items</p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">{formatCurrency(cat.total)}</p>
                        <p className="text-xs text-muted-foreground">{cat.percentage?.toFixed(1)}%</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Monthly Profit Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Monthly Profit Trend</CardTitle>
              <CardDescription>Net profit contributing to capital recovery</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {dashboard?.monthly_trend?.slice(-6).map((month, idx) => (
                  <div key={month.month} className="flex items-center gap-3">
                    <span className="text-sm w-20 text-muted-foreground">{month.month_name}</span>
                    <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
                      {month.profit >= 0 ? (
                        <div
                          className="h-full bg-green-500 rounded-full"
                          style={{ width: `${Math.min(100, (month.profit / (dashboard?.payback_projection?.avg_monthly_profit || 1)) * 50)}%` }}
                        />
                      ) : (
                        <div
                          className="h-full bg-red-500 rounded-full"
                          style={{ width: `${Math.min(100, (Math.abs(month.profit) / (dashboard?.payback_projection?.avg_monthly_profit || 1)) * 50)}%` }}
                        />
                      )}
                    </div>
                    <span className={`text-sm font-medium w-24 text-right ${month.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {formatCurrency(month.profit)}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Depreciation Summary */}
          {depreciation.schedule?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Depreciation Schedule</CardTitle>
                <CardDescription>Annual depreciation for capital assets</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 mb-4">
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Annual Depreciation</p>
                    <p className="text-xl font-bold">{formatCurrency(depreciation.total_annual)}</p>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Accumulated</p>
                    <p className="text-xl font-bold text-amber-600">{formatCurrency(depreciation.total_accumulated)}</p>
                  </div>
                  <div className="text-center p-3 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">Book Value</p>
                    <p className="text-xl font-bold text-green-600">{formatCurrency(depreciation.total_book_value)}</p>
                  </div>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Asset</TableHead>
                      <TableHead className="text-right">Cost</TableHead>
                      <TableHead className="text-right">Life</TableHead>
                      <TableHead className="text-right">Annual</TableHead>
                      <TableHead className="text-right">Book Value</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {depreciation.schedule?.map((item, idx) => (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">{item.item_name}</TableCell>
                        <TableCell className="text-right">{formatCurrency(item.original_cost)}</TableCell>
                        <TableCell className="text-right">{item.useful_life_years}y</TableCell>
                        <TableCell className="text-right text-amber-600">{formatCurrency(item.annual_depreciation)}</TableCell>
                        <TableCell className="text-right text-green-600">{formatCurrency(item.book_value)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Costs Tab */}
        <TabsContent value="costs" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Startup Costs</CardTitle>
                  <CardDescription>{costs.length} items totaling {formatCurrency(inv.total_investment)}</CardDescription>
                </div>
                <Button onClick={() => { setEditingCost(null); setShowCostModal(true); }}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {costs.length === 0 ? (
                <div className="text-center py-8">
                  <Package className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No startup costs recorded yet</p>
                  <Button className="mt-4" onClick={() => setShowCostModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Cost
                  </Button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead>Item</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Vendor</TableHead>
                        <TableHead className="text-right">Qty</TableHead>
                        <TableHead className="text-right">Unit Cost</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {costs.map((cost) => {
                        const IconComponent = categoryIcons[cost.category] || Folder;
                        return (
                          <TableRow key={cost.cost_id}>
                            <TableCell className="text-muted-foreground">{cost.date}</TableCell>
                            <TableCell>
                              <div>
                                <p className="font-medium">{cost.item_name}</p>
                                {cost.description && (
                                  <p className="text-xs text-muted-foreground truncate max-w-[200px]">{cost.description}</p>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="gap-1">
                                <IconComponent className="w-3 h-3" />
                                {categoryLabels[cost.category] || cost.category}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-muted-foreground">{cost.vendor || '-'}</TableCell>
                            <TableCell className="text-right">{cost.quantity}</TableCell>
                            <TableCell className="text-right">{formatCurrencyFull(cost.unit_cost)}</TableCell>
                            <TableCell className="text-right font-semibold">{formatCurrencyFull(cost.total_cost)}</TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => { setEditingCost(cost); setShowCostModal(true); }}
                                >
                                  <Edit2 className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="text-destructive"
                                  onClick={() => handleDeleteCost(cost.cost_id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Funding Tab */}
        <TabsContent value="funding" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Funding Sources</CardTitle>
                  <CardDescription>How your startup was funded</CardDescription>
                </div>
                <Button onClick={() => setShowFundingModal(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add Source
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Funding Gap Alert */}
              {inv.funding_gap > 0 && (
                <div className="mb-4 p-4 bg-amber-100 dark:bg-amber-900/30 rounded-lg flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-700 dark:text-amber-400">Funding Gap</p>
                    <p className="text-sm text-amber-600">
                      You have {formatCurrency(inv.funding_gap)} in costs not yet matched with funding sources.
                    </p>
                  </div>
                </div>
              )}

              {dashboard?.funding_sources?.length === 0 ? (
                <div className="text-center py-8">
                  <Wallet className="w-12 h-12 mx-auto mb-3 text-muted-foreground opacity-50" />
                  <p className="text-muted-foreground">No funding sources recorded yet</p>
                  <Button className="mt-4" onClick={() => setShowFundingModal(true)}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Funding Source
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {dashboard.funding_sources?.map((source) => (
                    <div key={source.funding_id} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-background rounded-lg">
                          {source.source_type === 'loan' ? (
                            <Building2 className="w-5 h-5 text-blue-600" />
                          ) : source.source_type === 'personal' ? (
                            <Wallet className="w-5 h-5 text-green-600" />
                          ) : (
                            <DollarSign className="w-5 h-5 text-purple-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{source.source_name}</p>
                          <p className="text-sm text-muted-foreground capitalize">
                            {source.source_type.replace('_', ' ')}
                            {source.interest_rate > 0 && ` • ${source.interest_rate}% interest`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <p className="text-xl font-bold">{formatCurrency(source.amount)}</p>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-destructive"
                          onClick={() => handleDeleteFunding(source.funding_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}

                  {/* Funding Total */}
                  <div className="flex items-center justify-between p-4 bg-primary/10 rounded-lg mt-4">
                    <p className="font-semibold">Total Funding</p>
                    <p className="text-xl font-bold text-primary">{formatCurrency(inv.total_funded)}</p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Sale Scenarios Tab */}
        <TabsContent value="scenarios" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                Sale Price Scenarios
              </CardTitle>
              <CardDescription>
                What happens if you sell the business at different valuations?
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {dashboard?.sale_scenarios?.map((scenario, idx) => (
                  <div
                    key={idx}
                    className={`p-4 rounded-lg border-2 ${
                      scenario.is_profitable
                        ? 'border-green-500/50 bg-green-50 dark:bg-green-900/20'
                        : 'border-red-500/50 bg-red-50 dark:bg-red-900/20'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <Badge variant={scenario.is_profitable ? 'default' : 'destructive'}>
                        {scenario.label}
                      </Badge>
                      <span className="text-xs text-muted-foreground">{scenario.multiplier}x</span>
                    </div>
                    <p className="text-2xl font-bold mb-2">{formatCurrency(scenario.sale_price)}</p>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Your Investment:</span>
                        <span>-{formatCurrency(inv.total_investment)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Profit Taken:</span>
                        <span className="text-green-600">+{formatCurrency(scenario.profit_taken)}</span>
                      </div>
                      <div className="border-t pt-1 mt-1 flex justify-between font-semibold">
                        <span>Net Position:</span>
                        <span className={scenario.is_profitable ? 'text-green-600' : 'text-red-600'}>
                          {scenario.net_position >= 0 ? '+' : ''}{formatCurrency(scenario.net_position)}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">ROI:</span>
                        <span className={scenario.is_profitable ? 'text-green-600' : 'text-red-600'}>
                          {scenario.roi_percent >= 0 ? '+' : ''}{scenario.roi_percent}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Breakeven Note */}
              <div className="mt-6 p-4 bg-muted/50 rounded-lg">
                <p className="text-sm">
                  <span className="font-semibold">Minimum sale price to break even:</span>{' '}
                  <span className="text-lg font-bold text-primary">
                    {formatCurrency(Math.max(0, inv.total_investment - recovery.recovered))}
                  </span>
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  This accounts for the {formatCurrency(recovery.recovered)} you've already recovered through operations.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <CostFormModal
        isOpen={showCostModal}
        onClose={() => { setShowCostModal(false); setEditingCost(null); }}
        onSave={handleSaveCost}
        editingCost={editingCost}
      />
      <FundingFormModal
        isOpen={showFundingModal}
        onClose={() => setShowFundingModal(false)}
        onSave={handleSaveFunding}
      />
    </div>
  );
}
